<template>
  <DataTable
  :loading="loading"
  :items="sortedTicketBooks"
  :headers="headers"
  :showSearch="false"
  :customCells="ticketBookCustomCells()"
  :propItemsPerPage="itemsPerPage"
  :itemsPerPageOptions="itemsPerPageOptions"
  @update:items-per-page="itemsPerPageUpdated">
    <template #status="{ item }">
      <Icon
        :icon="statusIcon(item)"
        :tooltipText="$t(item.status === TicketBookStatus.Inactive.value ? 'archived' : item.status === TicketBookStatus.InUse.value ? 'inUse' : 'available')"
        :iconColor="item.status === TicketBookStatus.Inactive.value ? 'red' : item.status === TicketBookStatus.InUse.value ? 'info' : 'success'"
      />
    </template>
    <template #name="{ item }">
      {{ item.name }}
    </template>
    <template #tract="{ item }">
      <ContractKeyTooltip
        v-if="item.contractId != null"
        :type="item.contractType"
        :fromAccount="item.contractFromAccount"
        :destinationAccount="item.contractDestinationAccount"
        :account="item.contractAccount"/>
      <span v-else>
        {{ item.tract }}
      </span>
    </template>
    <template #pages="{ item }">
      {{ pageRangeText(item) }}
    </template>
    <template #actions="{ item }">
      <Icon
      class="ml-2"
      icon="mdi-book-open-page-variant"
      :tooltipText="$t('viewPages')"
      @icon-clicked="$emit('view-pages', item)"/>
      <Icon
      v-if="searchedTicketIds[item.ticketBookId] != null"
      icon="mdi-clipboard-outline"
      :tooltipText="$t('viewTicket')"
      @icon-clicked="$emit('view-ticket', searchedTicketIds[item.ticketBookId])"/>
      <Icon
      v-if="!(readonly || accountId == undefined)"
      icon="mdi-pencil"
      :tooltipText="$t('editTicketBook')"
      @icon-clicked="$emit('edit-ticket-book', item)"/>
      <Icon
      v-if="!(readonly || accountId == undefined) && item.status !== TicketBookStatus.Inactive.value && item.totalPageCount > 0"
      icon="mdi-book-plus"
      :tooltipText="$t('extendTicketBook')"
      @icon-clicked="$emit('extend-ticket-book', item)"/>
      <Icon
      v-if="!readonly"
      icon="mdi-delete-forever"
      :tooltipText="$t('deleteTicketBook')"
      @icon-clicked="$emit('delete-ticket-book', item)"/>
    </template>
  </DataTable>
</template>

<script>
import { TicketBookStatus } from '@/utils/Enumerations'
import { ticketBookCustomCells } from '@/headers/Account'
import { pageRangeString } from '@/utils/NumericMutations'
import IconHelpers from '@/utils/IconHelpers'
import { dataTableItemsPerPage } from '@/utils/componentHelpers'
import { LocalStorageKeys } from '@/utils/LocalStorageActor'

const DEFAULT_ITEMS_PER_PAGE = 25

function storedItemsPerPage () {
  const stored = Number(localStorage.getItem(LocalStorageKeys.TICKET_BOOKS_ITEMS_PER_PAGE))
  return dataTableItemsPerPage.includes(stored) ? stored : DEFAULT_ITEMS_PER_PAGE
}

export default {
  name: 'TicketBookTable',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    ContractKeyTooltip: () => import('@/components/contract/ContractKeyTooltip.vue')
  },

  props: {
    loading: { type: Boolean, required: true },
    ticketBooks: { type: Array, required: true },
    headers: { type: Array, required: true },
    readonly: { type: Boolean, default: false },
    accountId: { type: Number, default: undefined },
    searchedTicketIds: { type: Object, default: () => ({}) },
    itemsPerPageOptions: { type: Array, default: undefined }
  },

  data () {
    return {
      TicketBookStatus,
      IconHelpers,
      itemsPerPage: this.itemsPerPageOptions?.[0] ?? storedItemsPerPage()
    }
  },

  computed: {
    sortedTicketBooks () {
      const sortOrder = {
        [TicketBookStatus.InUse.value]: 0,
        [TicketBookStatus.Available.value]: 1,
        [TicketBookStatus.Inactive.value]: 2
      }
      return [...this.ticketBooks]
        .map(tb => ({
          ...tb,
          mappedPages: { firstPage: tb.firstPage, lastPage: tb.lastPage },
          percentage: tb.totalPageCount > 0 ? (tb.totalPageCount - tb.availablePageCount) / tb.totalPageCount * 100 : 0
        }))
        .sort((a, b) => {
          if (a.status === b.status) { return a.firstPage - b.firstPage }
          const statusA = sortOrder[a.status]
          const statusB = sortOrder[b.status]
          return statusA - statusB
        })
    }
  },

  methods: {
    ticketBookCustomCells,
    pageRangeText (item) {
      return item.totalPageCount === 0
        ? this.$t('none')
        : pageRangeString(item.firstPage, item.lastPage)
    },
    itemsPerPageUpdated (val) {
      this.itemsPerPage = val
      if (this.itemsPerPageOptions == null) localStorage.setItem(LocalStorageKeys.TICKET_BOOKS_ITEMS_PER_PAGE, val)
    },
    statusIcon (item) {
      if (item.status === TicketBookStatus.Inactive.value) return 'mdi-archive'
      if (item.status !== TicketBookStatus.InUse.value) return 'mdi-check-circle'
      return IconHelpers.getIconForObjectType(item.contractId != null ? 'contract' : 'tract')
    }
  }
}
</script>
